import React from 'react';
import {
  DocPage, PageHeader, Section, SubSection, Prerequisites,
  Para, Callout,
} from '../components/DocBlocks';
import { useIndustry } from '../IndustryContext';
import './Grant.css';

const TOC = [
  { id: 'overview',      label: 'Overview'                        },
  { id: 'scope',         label: 'Scope'                           },
  { id: 'types',         label: 'Grant types'                     },
  { id: 'duration-only', label: 'Duration-only',       indent: true },
  { id: 'qty-only',      label: 'Quantity-only',       indent: true },
  { id: 'both-mode',     label: 'Duration and Quantity', indent: true },
  { id: 'stacking',      label: 'Stacking'                        },
  { id: 'on-bill',       label: 'How it appears on the bill'      },
  { id: 'limits',        label: 'Expiry and cycle limits'         },
  { id: 'trials',         label: 'Trials'                         },
  { id: 'trial-examples', label: 'Trial examples',    indent: true },
  { id: 'carry-forward',  label: 'Carry-forward',     indent: true },
  { id: 'trial-expiry',   label: 'Billing on expiry', indent: true },
];

// ── Type badge ────────────────────────────────────────────────────────────────

function TypeBadge({ type }) {
  const config = {
    duration: { label: 'Duration-only'        },
    quantity: { label: 'Quantity-only'         },
    both:     { label: 'Duration and Quantity' },
  };
  const { label } = config[type] || config.duration;
  return <span className={`gr-type-badge gr-type-badge--${type}`}>{label}</span>;
}

// ── Anatomy card ──────────────────────────────────────────────────────────────

function AnatomyCard({ g }) {
  const rows = [
    { key: 'Fee component', val: g.feeComponent },
    { key: 'Type',          val: <TypeBadge type={g.type} /> },
  ];
  if (g.duration)      rows.push({ key: 'Duration',       val: `${g.duration.days} days` });
  if (g.quantity)      rows.push({ key: 'Usage driver',   val: `${g.quantity.driver} — ${g.quantity.amount.toLocaleString()} ${g.quantity.unit}` });
  if (g.perCycleLimit) rows.push({ key: 'Per-cycle limit', val: `${g.perCycleLimit} ${g.quantity.unit}` });
  if (g.expiryDate)    rows.push({ key: 'Expiry date',    val: g.expiryDate });

  return (
    <div className="gr-anatomy">
      <div className="gr-anatomy-header">
        <span className="gr-anatomy-eyebrow">Grant</span>
        <span className="gr-anatomy-name">{g.name}</span>
        {g.description && <span className="gr-anatomy-desc">{g.description}</span>}
      </div>
      {rows.map((r) => (
        <div key={r.key} className="gr-anatomy-row">
          <span className="gr-anatomy-key">{r.key}</span>
          <span className="gr-anatomy-val">{r.val}</span>
        </div>
      ))}
    </div>
  );
}

// ── Bill lines mockup ─────────────────────────────────────────────────────────

function BillLines({ lines, note, eyebrow = 'Bill report excerpt' }) {
  return (
    <div className="gr-bill-wrap">
      <div className="gr-bill-eyebrow">{eyebrow}</div>
      <table className="gr-bill-table">
        <thead>
          <tr>
            <th className="gr-bill-th">Fee</th>
            <th className="gr-bill-th">Qty</th>
            <th className="gr-bill-th">Rate</th>
            <th className="gr-bill-th">Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} className={l.isGrant ? 'gr-bill-tr--grant' : ''}>
              <td className={`gr-bill-td ${l.isGrant ? 'gr-bill-td--grant-name' : 'gr-bill-td--fee-name'}`}>{l.name}</td>
              <td className="gr-bill-td gr-bill-td--mono">{l.qty}</td>
              <td className="gr-bill-td gr-bill-td--mono">{l.rate}</td>
              <td className={`gr-bill-td gr-bill-td--mono${l.isGrant ? ' gr-bill-td--zero' : ''}`}>{l.amount}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {note && <div className="gr-bill-note">{note}</div>}
    </div>
  );
}

// ── Cycle table (quantity-only example) ──────────────────────────────────────

function CycleTable({ cycles, unit, total, summary }) {
  return (
    <div className="gr-cycle-wrap">
      {summary && <div className="gr-cycle-summary">{summary}</div>}
      <table className="gr-cycle-table">
        <thead>
          <tr>
            <th className="gr-cycle-th">Billing cycle</th>
            <th className="gr-cycle-th">Usage</th>
            <th className="gr-cycle-th">Zero-rated</th>
            <th className="gr-cycle-th">Billed normally</th>
            <th className="gr-cycle-th">Cumulative used</th>
            <th className="gr-cycle-th">Grant remaining</th>
          </tr>
        </thead>
        <tbody>
          {cycles.map((c) => (
            <tr key={c.label} className={c.remaining === 0 ? 'gr-cycle-tr--exhausted' : ''}>
              <td className="gr-cycle-td gr-cycle-td--label">{c.label}</td>
              <td className="gr-cycle-td gr-cycle-td--mono">{c.usage} {unit}</td>
              <td className="gr-cycle-td gr-cycle-td--mono gr-cycle-td--zero">{c.zeroRated} {unit}</td>
              <td className="gr-cycle-td gr-cycle-td--mono">{c.billed > 0 ? `${c.billed} ${unit}` : '—'}</td>
              <td className="gr-cycle-td gr-cycle-td--mono">{c.cumulative} / {total} {unit}</td>
              <td className={`gr-cycle-td gr-cycle-td--mono${c.remaining === 0 ? ' gr-cycle-td--exhausted' : ''}`}>
                {c.remaining === 0 ? 'Exhausted' : `${c.remaining} ${unit}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Race visual (both mode) ───────────────────────────────────────────────────

function RaceVisual({ race }) {
  const daysPct    = Math.round((race.daysElapsed / race.totalDays)   * 100);
  const qtyPct     = Math.round((race.callsUsed   / race.totalCalls)  * 100);
  const durWon = race.exhaustedBy === 'duration';
  const qtyWon = race.exhaustedBy === 'quantity';

  return (
    <div className="gr-race">
      <div className={`gr-race-row${durWon ? ' gr-race-row--winner' : ''}`}>
        <span className="gr-race-label">Duration</span>
        <div className="gr-race-track">
          <div className="gr-race-bar gr-race-bar--duration" style={{ width: `${daysPct}%` }} />
        </div>
        <span className="gr-race-stat">{race.daysElapsed} of {race.totalDays} days</span>
        {durWon && <span className="gr-race-badge">exhausted first</span>}
      </div>
      <div className={`gr-race-row${qtyWon ? ' gr-race-row--winner' : ''}`}>
        <span className="gr-race-label">Quantity</span>
        <div className="gr-race-track">
          <div className="gr-race-bar gr-race-bar--quantity" style={{ width: `${qtyPct}%` }} />
        </div>
        <span className="gr-race-stat">{race.callsUsed.toLocaleString()} of {race.totalCalls.toLocaleString()} {race.unit}</span>
        {qtyWon && <span className="gr-race-badge">exhausted first</span>}
      </div>
    </div>
  );
}

// ── Stack timeline ────────────────────────────────────────────────────────────

// Hardcoded 90-day example: Trial (30 days) + 2 extensions (30 days each)
// Each grant is created while the previous is still running → queues up
const STACK_GRANTS = [
  {
    name: 'Trial Grant',
    sub: 'Created day 1',
    waiting: null,
    active: [0, 33.3],
  },
  {
    name: 'Extension — Month 1',
    sub: 'Created day 20',
    waiting: [22.2, 33.3],
    active: [33.3, 66.6],
  },
  {
    name: 'Extension — Month 2',
    sub: 'Created day 50',
    waiting: [55.5, 66.6],
    active: [66.6, 100],
  },
];

const EFFECTIVE_SEGMENTS = [
  { label: 'Trial Grant',         start: 0,    end: 33.3, accent: 'purple' },
  { label: 'Extension — Month 1', start: 33.3, end: 66.6, accent: 'teal'   },
  { label: 'Extension — Month 2', start: 66.6, end: 100,  accent: 'amber'  },
];

function StackTimeline() {
  return (
    <div className="gr-stack">
      {/* Day axis */}
      <div className="gr-stack-axis">
        {['Day 1', 'Day 30', 'Day 60', 'Day 90'].map((label, i) => (
          <span key={label} className="gr-stack-tick" style={{ left: `${i * 33.3}%` }}>{label}</span>
        ))}
      </div>

      {/* One row per grant */}
      {STACK_GRANTS.map((g, i) => (
        <div key={i} className="gr-stack-row">
          <div className="gr-stack-row-meta">
            <span className="gr-stack-row-name">{g.name}</span>
            <span className="gr-stack-row-sub">{g.sub}</span>
          </div>
          <div className="gr-stack-track">
            {g.waiting && (
              <div
                className="gr-stack-seg gr-stack-seg--waiting"
                style={{ left: `${g.waiting[0]}%`, width: `${g.waiting[1] - g.waiting[0]}%` }}
              />
            )}
            <div
              className="gr-stack-seg gr-stack-seg--active"
              style={{ left: `${g.active[0]}%`, width: `${g.active[1] - g.active[0]}%` }}
            >
              <span className="gr-stack-seg-label">active</span>
            </div>
          </div>
        </div>
      ))}

      {/* Effective / result row */}
      <div className="gr-stack-row gr-stack-row--effective">
        <div className="gr-stack-row-meta">
          <span className="gr-stack-row-name">Effective</span>
        </div>
        <div className="gr-stack-track">
          {EFFECTIVE_SEGMENTS.map((s, i) => (
            <div
              key={i}
              className={`gr-stack-seg gr-stack-seg--result gr-stack-seg--${s.accent}`}
              style={{ left: `${s.start}%`, width: `${s.end - s.start}%` }}
            >
              <span className="gr-stack-seg-label">{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Before / after ────────────────────────────────────────────────────────────

function BeforeAfter({ without, withGrant, context }) {
  return (
    <div className="gr-ba">
      {context && <div className="gr-ba-context">{context}</div>}
      <div className="gr-ba-cols">
        <div className="gr-ba-col gr-ba-col--before">
          <div className="gr-ba-label">Without grant</div>
          <div className="gr-ba-calc">{without}</div>
        </div>
        <div className="gr-ba-col gr-ba-col--after">
          <div className="gr-ba-label">With grant</div>
          <div className="gr-ba-calc">{withGrant}</div>
        </div>
      </div>
    </div>
  );
}

// ── Scope table ───────────────────────────────────────────────────────────────

function ScopeTable({ rows }) {
  return (
    <div className="gr-scope-wrap">
      <table className="gr-scope-table">
        <thead>
          <tr>
            <th className="gr-scope-th">Fee</th>
            <th className="gr-scope-th">Meter / Driver</th>
            <th className="gr-scope-th">Grant applies?</th>
            <th className="gr-scope-th">Effect</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.fee}>
              <td className={`gr-scope-td gr-scope-td--name${r.granted ? ' gr-scope-td--granted' : ''}`}>{r.fee}</td>
              <td className="gr-scope-td gr-scope-td--mono">{r.driver}</td>
              <td className="gr-scope-td">
                {r.granted
                  ? <span className="gr-badge-yes">✓ Yes</span>
                  : <span className="gr-badge-no">— No</span>
                }
              </td>
              <td className="gr-scope-td gr-scope-td--note">{r.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

// Scope example — shows same meter driving two fees,
// grant applies to one but not the other
const SCOPE_BILL_EXAMPLE = [
  { name: 'Starter Courier Allowance', isGrant: true,  qty: '200 kg', rate: '₹0',  amount: '₹0'     },
  { name: 'Shipping Fee',              isGrant: false, qty: '40 kg',  rate: '₹40', amount: '₹1,600' },
  { name: 'Fuel Surcharge',            isGrant: false, qty: '240 kg', rate: '₹2',  amount: '₹480'   },
];

// Hardcoded worked examples — always show all three types regardless of industry selector
const DURATION_EXAMPLE = {
  billLines: [
    { name: 'New Seller Waiver', isGrant: true, qty: '1', rate: '₹0', amount: '₹0' },
  ],
};

const QUANTITY_EXAMPLE = {
  cycles: [
    { label: 'Cycle 1', usage: 240, zeroRated: 200, billed: 40,  cumulative: 200, remaining: 300 },
    { label: 'Cycle 2', usage: 180, zeroRated: 180, billed: 0,   cumulative: 380, remaining: 120 },
    { label: 'Cycle 3', usage: 200, zeroRated: 120, billed: 80,  cumulative: 500, remaining: 0   },
  ],
  unit: 'kg', total: 500,
  billLines: [
    { name: 'Starter Courier Allowance', isGrant: true,  qty: '200 kg', rate: '₹0',  amount: '₹0'     },
    { name: 'Shipping Fee',              isGrant: false, qty: '40 kg',  rate: '₹40', amount: '₹1,600' },
  ],
};

const BOTH_EXAMPLE = {
  race: {
    daysElapsed: 18, totalDays: 30,
    callsUsed: 10000, totalCalls: 10000,
    unit: 'calls', exhaustedBy: 'quantity',
  },
  billLines: [
    { name: 'Launch Free Period', isGrant: true, qty: '10,000 calls', rate: '₹0', amount: '₹0' },
  ],
};

// ── Trial examples data ───────────────────────────────────────────────────────

const TRIAL_EXAMPLES = [
  { description: '30-day trial',                                            type: 'duration', duration: '30 days',         quantity: null,           perCycle: null,           carryForward: false },
  { description: '1st-month trial in a 12-month monthly contract',          type: 'duration', duration: '1 billing period', quantity: null,           perCycle: null,           carryForward: false },
  { description: '1,000 email trial',                                       type: 'quantity', duration: null,               quantity: '1,000 emails', perCycle: null,           carryForward: false },
  { description: '1,000 email trial, capped at 100 per month',              type: 'quantity', duration: null,               quantity: '1,000 emails', perCycle: '100 emails',   carryForward: false },
  { description: '1,000 email trial for 90 days',                           type: 'both',     duration: '90 days',          quantity: '1,000 emails', perCycle: null,           carryForward: false },
  { description: '1,000 email trial for 3 months, 500 emails/month cap',    type: 'both',     duration: '3 billing cycles', quantity: '1,000 emails', perCycle: '500 emails',   carryForward: false },
  { description: '3-month trial, 500 emails/month cap, unused carry forward', type: 'both',   duration: '3 billing cycles', quantity: '1,500 emails', perCycle: '500 emails',   carryForward: true  },
];

const CF_WITHOUT = [
  { cycle: 'Month 1', cap: 500, used: 300, zeroRated: 300, lost: 200, remaining: 700 },
  { cycle: 'Month 2', cap: 500, used: 500, zeroRated: 500, lost:   0, remaining: 200 },
  { cycle: 'Month 3', cap: 200, used: 250, zeroRated: 200, lost:   0, remaining:   0 },
];

const CF_WITH = [
  { cycle: 'Month 1', cap: 500, used: 300, zeroRated: 300, carried: 200, remaining: 1200 },
  { cycle: 'Month 2', cap: 700, used: 600, zeroRated: 600, carried:   0, remaining:  600 },
  { cycle: 'Month 3', cap: 600, used: 700, zeroRated: 600, carried:   0, remaining:    0 },
];

const PREPAID_EVENTS = [
  { day: 'Day 1',              kind: 'start',   desc: 'Contract starts. Billing period begins. Trial active — no invoice yet.' },
  { day: 'Day 17',             kind: 'expiry',  desc: 'Trial ends. System immediately invoices for days 17–30 (13 days pro-rata).' },
  { day: 'Day 30',             kind: 'regular', desc: 'Billing period closes normally.' },
  { day: 'Day 1 (next month)', kind: 'regular', desc: 'Next period starts. Full monthly prepaid invoice raised at start of period.' },
];

const POSTPAID_EVENTS = [
  { day: 'Day 1',                   kind: 'start',   desc: 'Contract starts. Billing period begins. Trial active — usage accrues, no charge.' },
  { day: 'Day 17',                  kind: 'expiry',  desc: 'Trial ends. Usage from day 17–30 accrues at full rate.' },
  { day: 'Day 1 (next month)',      kind: 'invoice', desc: 'Period closes. Invoice raised for 13 days of post-trial usage (pro-rata).' },
  { day: 'Day 1 (month after)',     kind: 'regular', desc: 'Full billing cycle. Invoice raised for complete period at full rate.' },
];

// ── Trial table ───────────────────────────────────────────────────────────────

function TrialTable() {
  return (
    <div className="gr-trial-table-wrap">
      <table className="gr-trial-table">
        <thead>
          <tr>
            <th className="gr-trial-th gr-trial-th--desc">Trial</th>
            <th className="gr-trial-th">Grant type</th>
            <th className="gr-trial-th">Duration</th>
            <th className="gr-trial-th">Quantity</th>
            <th className="gr-trial-th">Per-cycle limit</th>
            <th className="gr-trial-th">Carry-forward</th>
          </tr>
        </thead>
        <tbody>
          {TRIAL_EXAMPLES.map((ex, i) => (
            <tr key={i} className={ex.carryForward ? 'gr-trial-tr--highlight' : ''}>
              <td className="gr-trial-td gr-trial-td--desc">{ex.description}</td>
              <td className="gr-trial-td"><TypeBadge type={ex.type} /></td>
              <td className="gr-trial-td gr-trial-td--mono">{ex.duration ?? '—'}</td>
              <td className="gr-trial-td gr-trial-td--mono">{ex.quantity ?? '—'}</td>
              <td className="gr-trial-td gr-trial-td--mono">{ex.perCycle ?? '—'}</td>
              <td className="gr-trial-td gr-trial-td--center">{ex.carryForward ? <span className="gr-trial-yes">Yes</span> : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Carry-forward comparison ──────────────────────────────────────────────────

function CarryForwardComparison() {
  return (
    <div className="gr-cf-wrap">
      <div className="gr-cf-col">
        <div className="gr-cf-heading gr-cf-heading--without">Without carry-forward <span className="gr-cf-config">1,000 emails · 500/month</span></div>
        <table className="gr-cf-table">
          <thead>
            <tr>
              <th className="gr-cf-th">Cycle</th>
              <th className="gr-cf-th">Cap</th>
              <th className="gr-cf-th">Used</th>
              <th className="gr-cf-th">Zero-rated</th>
              <th className="gr-cf-th gr-cf-th--lost">Lost</th>
              <th className="gr-cf-th">Remaining</th>
            </tr>
          </thead>
          <tbody>
            {CF_WITHOUT.map((r) => (
              <tr key={r.cycle}>
                <td className="gr-cf-td gr-cf-td--label">{r.cycle}</td>
                <td className="gr-cf-td gr-cf-td--mono">{r.cap}</td>
                <td className="gr-cf-td gr-cf-td--mono">{r.used}</td>
                <td className="gr-cf-td gr-cf-td--mono gr-cf-td--zero">{r.zeroRated}</td>
                <td className="gr-cf-td gr-cf-td--mono gr-cf-td--lost">{r.lost > 0 ? r.lost : '—'}</td>
                <td className={`gr-cf-td gr-cf-td--mono${r.remaining === 0 ? ' gr-cf-td--exhausted' : ''}`}>
                  {r.remaining === 0 ? 'Exhausted' : r.remaining}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="gr-cf-note">300 emails go unused in month 1 — they expire, not carried over.</div>
      </div>

      <div className="gr-cf-col">
        <div className="gr-cf-heading gr-cf-heading--with">With carry-forward <span className="gr-cf-config">1,500 emails · 500/month</span></div>
        <table className="gr-cf-table">
          <thead>
            <tr>
              <th className="gr-cf-th">Cycle</th>
              <th className="gr-cf-th">Cap</th>
              <th className="gr-cf-th">Used</th>
              <th className="gr-cf-th">Zero-rated</th>
              <th className="gr-cf-th gr-cf-th--carried">Carried in</th>
              <th className="gr-cf-th">Remaining</th>
            </tr>
          </thead>
          <tbody>
            {CF_WITH.map((r) => (
              <tr key={r.cycle}>
                <td className="gr-cf-td gr-cf-td--label">{r.cycle}</td>
                <td className="gr-cf-td gr-cf-td--mono gr-cf-td--cap">{r.cap}</td>
                <td className="gr-cf-td gr-cf-td--mono">{r.used}</td>
                <td className="gr-cf-td gr-cf-td--mono gr-cf-td--zero">{r.zeroRated}</td>
                <td className="gr-cf-td gr-cf-td--mono gr-cf-td--carried">{r.carried > 0 ? `+${r.carried}` : '—'}</td>
                <td className={`gr-cf-td gr-cf-td--mono${r.remaining === 0 ? ' gr-cf-td--exhausted' : ''}`}>
                  {r.remaining === 0 ? 'Exhausted' : r.remaining}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="gr-cf-note">200 unused emails from month 1 roll over — month 2 cap rises to 700.</div>
      </div>
    </div>
  );
}

// ── Expiry billing timeline ───────────────────────────────────────────────────

function ExpiryCase({ label, accent, events }) {
  return (
    <div className={`gr-expiry-case gr-expiry-case--${accent}`}>
      <div className="gr-expiry-label">{label}</div>
      <div className="gr-expiry-timeline">
        {events.map((ev, i) => (
          <div key={i} className={`gr-expiry-event gr-expiry-event--${ev.kind}`}>
            <div className="gr-expiry-dot" />
            <div className="gr-expiry-event-body">
              <span className="gr-expiry-day">{ev.day}</span>
              <span className="gr-expiry-desc">{ev.desc}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Grant({ onNavigate }) {
  const { industry } = useIndustry();
  const g = industry.grant;

  return (
    <DocPage toc={TOC}>
      <PageHeader
        eyebrow="Customers & Contracts"
        title="Grants"
        description="A Grant zero-rates a specific fee for a customer — up to a usage cap, a time window, or both. The line still appears on the invoice at zero."
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[
          { id: 'meter', label: 'Meters' },
          { id: 'fee',   label: 'Fees'   },
          { id: 'contract',        label: 'Contracts'        },
        ]}
      />

      <AnatomyCard g={g} />

      <Section id="overview" title="Overview">
        <Para>
          A grant is attached to a specific fee on a contract. When active, the
          billing engine produces a zero-rate line item in the bill report for all usage
          the grant covers. The customer sees what was waived and at what quantity — but
          pays ₹0 for it.
        </Para>
        <Para>
          Either a duration or a quantity is required. Both can be set together:
        </Para>
        <div className="gr-overview-type-table">
          <table className="gr-overview-table">
            <thead>
              <tr>
                <th className="gr-overview-th">Type</th>
                <th className="gr-overview-th">What is zero-rated</th>
                <th className="gr-overview-th">Grant ends when</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="gr-overview-td"><TypeBadge type="duration" /></td>
                <td className="gr-overview-td">All usage on the fee during the grant window</td>
                <td className="gr-overview-td">Duration elapses, or the company manually ends it early (expiry date)</td>
              </tr>
              <tr>
                <td className="gr-overview-td"><TypeBadge type="quantity" /></td>
                <td className="gr-overview-td">Usage up to the specified quantity per usage driver</td>
                <td className="gr-overview-td">Quantity consumed, or the company manually ends it early (expiry date)</td>
              </tr>
              <tr>
                <td className="gr-overview-td"><TypeBadge type="both" /></td>
                <td className="gr-overview-td">All usage (up to quantity limit per driver)</td>
                <td className="gr-overview-td">Duration elapsed <em>or</em> quantity consumed — whichever happens first, or the company manually ends it early</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      <Section id="scope" title="Scope — fee, not meter">
        <Para>
          A grant is attached to a specific fee. A meter can drive
          multiple fees on the same product — but the grant only applies to the
          fee it was explicitly created on. Other fees that share the
          same meter are unaffected and are billed at their normal rates.
        </Para>
        <ScopeTable rows={g.scopeTable} />
        <Para>
          For example: 240 kg of Courier Weight is recorded in a billing cycle. The grant
          (200 kg cycle cap) zero-rates 200 kg on Shipping Fee. Fuel Surcharge is driven
          by the same Courier Weight but is not in the grant scope — it bills all 240 kg
          at the full rate:
        </Para>
        <BillLines
          eyebrow="Cycle 1 — 240 kg Courier Weight recorded, grant scoped to Shipping Fee only"
          lines={SCOPE_BILL_EXAMPLE}
          note="Same 240 kg drives both fees — grant applies only to Shipping Fee"
        />
        <Callout type="tip" title="Why per fee?">
          Different fees represent economically distinct charges. Zero-rating
          courier weight on a Shipping Fee should not automatically waive a Fuel Surcharge
          that happens to be measured in the same unit — those are separate cost lines with
          separate pricing logic.
        </Callout>
      </Section>

      <Section id="types" title="Grant types">

        <SubSection id="duration-only" title="Duration-only">
          <Para>
            The billing engine bills the fee at ₹0 for all usage recorded during the grant
            window. The window begins when the grant starts and lasts for the configured number
            of days, unless the company ends it early.
          </Para>
          <Para>
            Grant: <strong>New Seller Waiver</strong> — Transaction Fee, 30 days.
            The customer records $4,500 GMV during the window (Transaction Fee rate: 2%):
          </Para>
          <BeforeAfter
            context="$4,500 GMV recorded during the 30-day grant window"
            without="$4,500 × 2% = $90"
            withGrant="$0 — entire period zero-rated"
          />
          <Para>
            Because the entire fee is waived for the period, the bill report shows a single
            zero-rate line with <strong>qty = 1</strong>. There is no per-unit count —
            the grant period itself is what is recorded.
          </Para>
          <BillLines
            eyebrow="Bill — New Seller Waiver active (30-day duration grant, all usage zero-rated)"
            lines={DURATION_EXAMPLE.billLines}
            note="qty: 1 — represents the granted period, not a unit count"
          />
          <Callout type="info" title="No quantity cap">
            A duration grant does not limit by usage volume. Whether the customer records
            100 units or 100,000 units during the window, all of it is zero-rated. The only
            limits are the duration itself and any early manual termination by the company.
          </Callout>
        </SubSection>

        <SubSection id="qty-only" title="Quantity-only">
          <Para>
            The billing engine zero-rates usage up to the specified quantity for each usage
            driver on the fee. Once that quantity is consumed, the remaining usage in that
            period — and all usage in subsequent periods — is billed at the normal rate.
          </Para>
          <Para>
            In this example: a 500 kg grant on Shipping Fee with a 200 kg per-cycle cap.
            Usage depletes the grant across three billing cycles:
          </Para>
          <CycleTable
            cycles={QUANTITY_EXAMPLE.cycles}
            unit={QUANTITY_EXAMPLE.unit}
            total={QUANTITY_EXAMPLE.total}
            summary="Starter Courier Allowance — 500 kg total · 200 kg per-cycle cap"
          />
          <BeforeAfter
            context="Cycle 1 — 240 kg recorded, rate ₹40/kg, 200 kg per-cycle cap"
            without="240 kg × ₹40 = ₹9,600"
            withGrant="40 kg × ₹40 = ₹1,600 (200 kg zero-rated)"
          />
          <Para>
            The bill report for Cycle 1:
          </Para>
          <BillLines
            eyebrow="Cycle 1 bill — 240 kg recorded, 200 kg zero-rated (per-cycle cap), 40 kg billed"
            lines={QUANTITY_EXAMPLE.billLines}
            note="qty: 200 kg — the zero-rated unit count for this cycle"
          />
          <Callout type="info" title="Per-cycle limit">
            An optional per-cycle limit caps how much of the total grant can be consumed in
            a single billing cycle. In this example, the 200 kg cycle cap prevents the
            customer from exhausting the full 500 kg allowance in the first cycle. See{' '}
            <em>Expiry and cycle limits</em> below for full details.
          </Callout>
        </SubSection>

        <SubSection id="both-mode" title="Duration and Quantity">
          <Para>
            When both a duration and a quantity are set, the grant zero-rates usage until
            either the duration elapses or the quantity is consumed — whichever happens
            first. The company can also end the grant early at any point.
          </Para>
          <Para>
            In this example: a 30-day or 10,000 API calls grant. The customer reaches 10,000
            calls at day 18, exhausting the quantity before the 30-day window closes. The
            grant ends at day 18; days 19–30 are billed normally.
          </Para>
          <RaceVisual race={BOTH_EXAMPLE.race} />
          <BeforeAfter
            context="Days 1–18: 10,000 calls used (quantity exhausted). Days 19–30: billed normally. Rate: ₹0.001/call."
            without="10,000 calls × ₹0.001 = ₹10 (days 1–18 alone)"
            withGrant="₹0 — all 10,000 calls zero-rated before day 30"
          />
          <Para>
            Because quantity was exhausted first, the bill shows the actual zero-rated
            call count:
          </Para>
          <BillLines
            eyebrow="Bill — Launch Free Period ended day 18 (10,000 calls exhausted; 12 days of 30 remained)"
            lines={BOTH_EXAMPLE.billLines}
            note="qty: 10,000 calls — quantity exhausted first (duration had 12 days remaining)"
          />
          <Callout type="tip">
            If the duration had elapsed first — say, 30 days passed with only 5,000 calls
            used — the bill line would show qty: 5,000 calls (the actual count zero-rated
            during the window), not 10,000.
          </Callout>
        </SubSection>

      </Section>

      <Section id="stacking" title="Stacking">
        <Para>
          Multiple grants can be added to the same fee. Only one is active at any
          point in time — the billing engine applies them in creation order. When the active
          grant expires, the next earliest-created grant automatically becomes active.
        </Para>
        <Para>
          If multiple grants have overlapping configured windows, the one created first takes
          priority and runs to completion. The others wait in a queue regardless of their
          configured start dates.
        </Para>
        <StackTimeline />
        <Para>
          In the example above, all three grants are created while the previous one is still
          running. The second and third are created before they are needed — they sit in the
          queue and activate in sequence as each predecessor expires.
        </Para>
        <Callout type="tip" title="Auditable trial extensions">
          Stacking is the standard way to extend a trial or free period. Create the extension
          grant before the original expires — it queues up and activates automatically, with no
          gap in coverage. The full grant history is visible on the contract, making extensions
          auditable: you can see every grant that was created, when each was active, and for how
          long.
        </Callout>
      </Section>

      <Section id="on-bill" title="How it appears on the bill">
        <Para>
          Zero-rated usage always appears as a named fee line item on the bill report.
          The billing engine uses the grant's name as the line item's fee name and the
          grant's description as the fee description.
        </Para>
        <div className="gr-on-bill-table-wrap">
          <table className="gr-overview-table">
            <thead>
              <tr>
                <th className="gr-overview-th">Bill line field</th>
                <th className="gr-overview-th">Duration grant</th>
                <th className="gr-overview-th">Quantity grant</th>
              </tr>
            </thead>
            <tbody>
              {[
                { field: 'Fee name',        dur: 'Grant name',        qty: 'Grant name'             },
                { field: 'Fee description', dur: 'Grant description', qty: 'Grant description'      },
                { field: 'Qty',             dur: '1',                 qty: 'Zero-rated unit count'  },
                { field: 'Rate',            dur: '₹0',                qty: '₹0'                     },
                { field: 'Amount',          dur: '₹0',                qty: '₹0'                     },
              ].map((r) => (
                <tr key={r.field}>
                  <td className="gr-overview-td gr-overview-td--label">{r.field}</td>
                  <td className="gr-overview-td gr-overview-td--mono">{r.dur}</td>
                  <td className="gr-overview-td gr-overview-td--mono">{r.qty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Callout type="info" title="The fee's regular line also appears (quantity grants)">
          For quantity grants, usage not covered by the grant is billed normally and appears
          as the fee's regular line item. The bill shows both: the grant line (₹0,
          zero-rated count) and the fee line (normal rate, remaining count). Duration grants
          zero-rate all usage for the period — no regular fee line appears.
        </Callout>
      </Section>

      <Section id="limits" title="Expiry and cycle limits">
        <SubSection id="expiry" title="Expiry date — manual termination">
          <Para>
            The expiry date is how the company manually ends an active grant before it
            would naturally expire. Setting an expiry date terminates the grant on that
            date regardless of how much duration or quantity remains. Usage from the expiry
            date onwards is billed at the normal rate.
          </Para>
          <Para>
            This is a company-controlled action — it is not a system-calculated date. The
            company decides when to set it and can update it at any time while the grant
            is active.
          </Para>
          <Callout type="tip">
            Use expiry dates to revoke a grant early — for example, if a trial customer
            converts to a paid plan mid-period, or if a promotional grant needs to be
            withdrawn before its duration would naturally end.
          </Callout>
        </SubSection>
        <SubSection id="cycle-limit" title="Per-cycle limit">
          <Para>
            Quantity grants support an optional per-cycle limit. This caps how many units
            can be zero-rated in any single billing cycle. The overall grant quantity is
            still depleted by the zero-rated amount each cycle — the limit smooths
            consumption across periods rather than allowing the entire allowance to be
            consumed in one cycle.
          </Para>
          <Para>
            In Cycle 3 of the example above, only 120 kg remained of the 500 kg grant.
            Even though the cycle limit was 200 kg, the grant was exhausted at 120 kg —
            the lower of the two limits always applies.
          </Para>
          <Callout type="info" title="Per-cycle limits are for quantity grants only">
            Duration-only grants zero-rate all usage for the entire window. There is no
            per-unit quantity to cap.
          </Callout>
        </SubSection>
      </Section>

      <Section id="trials" title="Trials">
        <Para>
          A trial is a grant added to the relevant fees of a contract. The grant
          determines what is zero-rated, for how long, and up to what usage limit. Because
          trials are grants, all grant behaviours apply: they appear as named ₹0 line items
          on the bill, they stack (a queued grant activates as soon as the previous expires),
          and they are scoped per fee. A contract can have some fees on trial while
          others bill at full rate from day one.
        </Para>
        <Callout type="tip" title="Extending a trial">
          To extend a running trial, create another grant on the same fee before
          the current one expires. It queues automatically and activates the moment the first
          ends — no gap in coverage, full audit trail.
        </Callout>

        <SubSection id="trial-examples" title="Trial examples">
          <Para>
            Each row maps a common trial requirement to the grant configuration that
            implements it. "Duration" is the grant's duration field; "Quantity" is the
            total usage budget; "Per-cycle limit" caps how much of that budget can be
            consumed in one billing cycle.
          </Para>
          <TrialTable />
        </SubSection>

        <SubSection id="carry-forward" title="Carry-forward">
          <Para>
            When a per-cycle limit is set, unused allowance in a cycle is normally lost —
            the cap resets to the configured limit at the start of each new cycle. With{' '}
            <strong>carry-forward-unused</strong> enabled, the unused portion rolls over
            and raises the next cycle's effective cap.
          </Para>
          <Para>
            In the example below, 300 emails are used in month 1 against a 500/month cap.
            Without carry-forward, the 200 unused emails expire. With carry-forward, they
            roll into month 2, raising its cap to 700.
          </Para>
          <CarryForwardComparison />
          <Callout type="note" title="Total budget">
            Carry-forward redistributes when the allowance can be used — it does not
            increase the overall grant budget. The quantity field must cover the full
            intended allowance across all cycles (3 months × 500/month = 1,500 in the
            example above).
          </Callout>
        </SubSection>

        <SubSection id="trial-expiry" title="Billing on trial expiry">
          <Para>
            When a trial expires mid-cycle, the fee does not wait until the next period
            to start charging. The billing engine immediately pro-rates the charge for
            the remainder of the current cycle. From the next full period, billing runs
            at the normal rate.
          </Para>
          <ExpiryCase
            label="Prepaid — fee started 1st, trial expired 17th"
            accent="prepaid"
            events={PREPAID_EVENTS}
          />
          <ExpiryCase
            label="Postpaid — fee started 1st, trial expired 17th"
            accent="postpaid"
            events={POSTPAID_EVENTS}
          />
          <Callout type="note" title="Why pro-rate?">
            Without pro-rating, a customer whose trial expired mid-cycle would either
            get a free partial cycle or be charged for days when the trial was still
            active. Pro-rating charges only the post-trial portion and keeps subsequent
            invoices on clean period boundaries.
          </Callout>
        </SubSection>
      </Section>

    </DocPage>
  );
}
